// MUI Components
import React, { useCallback, useMemo } from "react";

// Components
import VisualFieldScreeningCard from "../VisualFieldScreeningCard";
import VisualFieldThresholdCard from "../VisualFieldThresholdCard";
import EstermanCard from "../EstermanCard";
import Full120Card from "../Full120Card";
import VisualAcuityCard from "../VisualAcuityCard";
import ContrastSensitivityCard from "../ContrastSensitivityCard";
import ColorVisionCard from "../ColorVisionCard";
import SynoptophoreCard from "../SynoptophoreCard";
import RAPDCard from "../RAPDCard";

// Utilities
import { examCardEnums, eyeTypes } from "../../../../../enums";
import useClinic from "../../../../../hooks/useClinic";
import { useExamListContext } from "../../../../../contexts/ExamListContext";

const ExamSliderCard = (props) => {
  const { examAlgorithm } = props;
  const { defaultExamOrder, examCards, actions } = useExamListContext();
  const { setExamCards } = actions;

  // clinic context
  const { clinicLicensing } = useClinic();
  const canUseExamPackaging = clinicLicensing?.canUseExamPackaging;
  const maxCardArrayLength = canUseExamPackaging ? 5 : 1;

  const selectedExamCards = useMemo(
    () => examCards.selected,
    [examCards.selected]
  );

  const updateExamCardProperty = useCallback(
    (propertyName, value) => {
      setExamCards((prev) => {
        // get the index of the exam to update
        const indexToUpdate = prev.selected.findIndex(
          (exam) => exam.examFieldType === examAlgorithm
        );

        // update the correct exam card in the array
        // with the new property value
        if (indexToUpdate > -1) {
          let examToUpdate = prev.selected[indexToUpdate];
          examToUpdate = { ...examToUpdate, [propertyName]: value };
          prev.selected[indexToUpdate] = examToUpdate;
        }

        return { ...prev, selected: [...prev.selected] };
      });
    },
    [setExamCards, examAlgorithm]
  );

  // handles changing the Both/Right/Left radio buttons on the exam card
  const onChangeEye = useCallback(
    (value) => {
      const { eyeLeft, eyeRight, eyeBoth } = eyeTypes;
      if (value === eyeBoth) {
        updateExamCardProperty("eyeCheckboxes", {
          right: true,
          left: true,
        });
      } else if (value === eyeLeft) {
        updateExamCardProperty("eyeCheckboxes", {
          right: false,
          left: true,
        });
      } else if (value === eyeRight) {
        updateExamCardProperty("eyeCheckboxes", {
          right: true,
          left: false,
        });
      } else {
        updateExamCardProperty("eyeCheckboxes", {
          right: false,
          left: false,
        });
      }
    },
    [updateExamCardProperty]
  );

  // handles changing the Right First toggle on the exam card
  const onChangeSectionOrder = useCallback(
    (isRightFirst) => {
      updateExamCardProperty("sectionOrder", {
        right: isRightFirst ? 0 : 1,
        left: isRightFirst ? 1 : 0,
      });
    },
    [updateExamCardProperty]
  );

  // handles changing the toggle on the exam card
  const onToggleCard = useCallback(
    (e, defaultCardValues) => {
      // untoggled: remove card from selected array
      if (!e.target.checked) {
        setExamCards((prev) => {
          const selected = prev.selected.filter(
            (examCard) => examCard.examFieldType !== examAlgorithm
          );
          const deselected = defaultExamOrder.filter(
            (defaultExam) =>
              !selected.some(
                (selectedExam) =>
                  defaultExam.algorithmPath === selectedExam.examFieldType
              )
          );
          return { selected: [...selected], deselected: [...deselected] };
        });
      }
      // toggled: add card (with default values) to the selected array
      else {
        setExamCards((prev) => {
          // if the card limit is reached, remove the oldest object
          if (prev.selected.length === maxCardArrayLength) {
            prev.selected.shift();
          }

          const selected = [...prev.selected, defaultCardValues];
          const deselected = defaultExamOrder.filter(
            (defaultExam) =>
              !selected.some(
                (selectedExam) =>
                  defaultExam.algorithmPath === selectedExam.examFieldType
              )
          );
          return { selected: [...selected], deselected: [...deselected] };
        });
      }
    },
    [examAlgorithm, maxCardArrayLength, setExamCards, defaultExamOrder]
  );

  switch (examAlgorithm) {
    case examCardEnums.VISUAL_FIELD_SCREENING.algorithmPath:
      return (
        <VisualFieldScreeningCard
          {...props}
          selectedExamCards={selectedExamCards}
          onChangeEye={onChangeEye}
          onChangeSectionOrder={onChangeSectionOrder}
          onToggleCard={onToggleCard}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.VISUAL_FIELD_THRESHOLDING.algorithmPath:
      return (
        <VisualFieldThresholdCard
          {...props}
          selectedExamCards={selectedExamCards}
          onChangeEye={onChangeEye}
          onChangeSectionOrder={onChangeSectionOrder}
          onToggleCard={onToggleCard}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.VISUAL_FIELD_ESTERMAN.algorithmPath:
      return (
        <EstermanCard
          {...props}
          selectedExamCards={selectedExamCards}
          onChangeEye={onChangeEye}
          onChangeSectionOrder={onChangeSectionOrder}
          onToggleCard={onToggleCard}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.VISUAL_FIELD_FULL_120.algorithmPath:
      return (
        <Full120Card
          {...props}
          selectedExamCards={selectedExamCards}
          onChangeEye={onChangeEye}
          onChangeSectionOrder={onChangeSectionOrder}
          onToggleCard={onToggleCard}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.VISUAL_ACUITY.algorithmPath:
      return (
        <VisualAcuityCard
          {...props}
          selectedExamCards={selectedExamCards}
          onChangeEye={onChangeEye}
          onChangeSectionOrder={onChangeSectionOrder}
          onToggleCard={onToggleCard}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.CONTRAST_SENSITIVITY.algorithmPath:
      return (
        <ContrastSensitivityCard
          {...props}
          selectedExamCards={selectedExamCards}
          onChangeEye={onChangeEye}
          onChangeSectionOrder={onChangeSectionOrder}
          onToggleCard={onToggleCard}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.COLOR_VISION.algorithmPath:
      return (
        <ColorVisionCard
          {...props}
          selectedExamCards={selectedExamCards}
          onChangeEye={onChangeEye}
          onChangeSectionOrder={onChangeSectionOrder}
          onToggleCard={onToggleCard}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.SYNOPTOPHORE.algorithmPath:
      return (
        <SynoptophoreCard
          {...props}
          selectedExamCards={selectedExamCards}
          onChangeEye={onChangeEye}
          onChangeSectionOrder={onChangeSectionOrder}
          onToggleCard={onToggleCard}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.RAPD.algorithmPath:
      return (
        <RAPDCard
          {...props}
          selectedExamCards={selectedExamCards}
          onChangeEye={onChangeEye}
          onChangeSectionOrder={onChangeSectionOrder}
          onToggleCard={onToggleCard}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    default:
      return <></>;
  }
};

export default ExamSliderCard;
